import React, { useState, useEffect, useCallback } from 'react';
import { Upload, X, FileText, AlertCircle } from 'lucide-react';
import * as utils from '../lib/utils.js'
import { useNotification } from '../lib/NotificationProvider.js';

const FileUpload = ({ onClose, shipment, forwarder, setDocumentsList }) => {
  const { showNotification } = useNotification();
  const [dragActive, setDragActive] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [fileTypes, setFileTypes] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const documentTypes = [
    "Select a document type...", // Placeholder option
    "Airway Bill",
    "Bill of Lading",
    "Commercial Invoice",
    "House Bill",
    "Manifest",
    "Master Bill",
    "Packing List",
    "Proof of Delivery",
    "Other"
  ];

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const droppedFiles = [...e.dataTransfer.files];
    setFiles(droppedFiles);
    
    // Initialize document types for each file
    const initialTypes = {};
    const initialErrors = {};
    droppedFiles.forEach((file, index) => {
      initialTypes[index] = ""; // Empty string to force selection
      initialErrors[index] = true; // Mark as error initially
    });
    setFileTypes(initialTypes);
    setValidationErrors(initialErrors);
  };

  const handleFileInput = (e) => {
    const selectedFiles = [...e.target.files];
    setFiles(selectedFiles);
    
    // Initialize document types for each file
    const initialTypes = {};
    const initialErrors = {};
    selectedFiles.forEach((file, index) => {
      initialTypes[index] = ""; // Empty string to force selection
      initialErrors[index] = true; // Mark as error initially
    });
    setFileTypes(initialTypes);
    setValidationErrors(initialErrors);
  };

  const handleDocumentTypeChange = (index, type) => {
    const newFileTypes = {
      ...fileTypes,
      [index]: type
    };
    setFileTypes(newFileTypes);
    const newErrors = {
      ...validationErrors,
      [index]: type === "Select a document type..." || type === ""
    };
    setValidationErrors(newErrors);
  };

  const validateAllDocumentTypes = () => {
    return !Object.values(validationErrors).some(error => error);
  };

  const onUploadClick = useCallback(() => {
    console.log('Upload button clicked');
    if (validateAllDocumentTypes()) {
      handleUpload();
    } else {
      console.log('Validation failed: Document types are required');
    }
  }, [files, fileTypes, validationErrors]);

  const handleUpload = async () => {
    console.log('Handle upload triggered');
    if (files.length === 0) {
      console.log('No files to upload');
      return;
    }
    
    setUploading(true);
    console.log('Starting upload process');
    
    try {
        const formData = new FormData();
        files.forEach((file, index) => {
          formData.append('files', file);
          formData.append('documentType', fileTypes[index]);
          formData.append('forwarder', forwarder);
          formData.append('shipment', shipment);
        });
        console.log('Files appended to FormData');

        const response  = await utils.uploadFiles(formData);
        const mappedDocs = response.data.documents.map((doc, index) => ({
          id: doc.id,
          webUrl: doc.webUrl,
          fields: {
              FileLeafRef: doc.name || doc.fileName,
              DocumentType: fileTypes[index],
              FileSizeDisplay: utils.formatFileSize(doc.size),
              Modified: doc.createdDateTime,
              ShipmentNumber: shipment
          }
        }));

        const messages = Array.isArray(response.data?.message) ? response.data.message : [];
        const failedMessages = messages.filter(m => m?.status === 'error');

        if (failedMessages.length > 0) {
          const detail = failedMessages.map(m => m.message || m.file).filter(Boolean).join('; ');
          showNotification('error', `Some files failed to upload: ${detail}`);
          if (mappedDocs.length > 0) {
            setDocumentsList(prevDocs => [...prevDocs, ...mappedDocs]);
          }
          return;
        }

        console.log('Upload response:', mappedDocs, response.data);
        setDocumentsList(prevDocs => [...prevDocs, ...mappedDocs]);
        showNotification('success', `${mappedDocs.length} file${mappedDocs.length === 1 ? '' : 's'} uploaded successfully`);
        onClose();
    } catch (error) {
      console.error('Upload error:', error);
      const message =
        error.response?.data?.message
        || error.response?.data?.error?.message
        || error.message
        || 'Failed to upload files. Please try again.';
      showNotification('error', typeof message === 'string' ? message : 'Failed to upload files. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Upload Documents</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={20} />
          </button>
        </div>
        
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center ${
            dragActive ? 'border-accent-500 bg-accent-50' : 'border-gray-300'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <Upload className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-sm text-gray-600">
            Drag and drop files here, or
            <label className="mx-1 text-accent-600 hover:text-accent-500 cursor-pointer">
              browse
              <input
                type="file"
                className="hidden"
                multiple
                onChange={handleFileInput}
              />
            </label>
          </p>
        </div>

        {files.length > 0 && (
          <div className="mt-4">
            <h3 className="text-sm font-medium mb-2">Selected files:</h3>
            <ul className="text-sm text-gray-600 space-y-4">
              {files.map((file, index) => (
                <li key={index} className="py-3 px-4 bg-gray-50 rounded-md">
                  <div className="flex items-center mb-3">
                    <FileText size={16} className="text-gray-400 mr-2" />
                    <span className="font-medium">{file.name}</span>
                    <span className="ml-2 text-xs text-gray-500">({(file.size / 1024).toFixed(1)} KB)</span>
                  </div>
                  
                  <div className="mb-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Document Type <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <select
                        value={fileTypes[index] || ""}
                        onChange={(e) => handleDocumentTypeChange(index, e.target.value)}
                        className={`block w-full text-sm rounded-md shadow-sm focus:ring-accent-500 focus:border-accent-500 ${
                          validationErrors[index] 
                            ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                            : 'border-gray-300'
                        }`}
                        required
                      >
                        {documentTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                      {validationErrors[index] && (
                        <div className="mt-1 flex items-center text-red-500 text-xs">
                          <AlertCircle size={12} className="mr-1" />
                          Document type is required
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800"
            disabled={uploading}
            type="button"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              console.log('Button clicked');
              onUploadClick();
            }}
            type="button"
            disabled={files.length === 0 || uploading || !validateAllDocumentTypes()}
            className={`px-4 py-2 text-sm font-medium text-white rounded-md flex items-center ${
              files.length === 0 || uploading || !validateAllDocumentTypes()
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-accent-600 hover:bg-accent-700'
            }`}
          >
            {uploading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Uploading...
              </>
            ) : (
              <>
                <Upload size={16} className="mr-2" />
                Upload
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FileUpload;